import { useEffect, useRef, useState } from "react";
import { productSiteOrigin, productSiteUrl } from "../config/siteOrigin";

export const HOST_TRANSFER_LOOP_TTL_MS = 30_000;

const DESTINATION_ORIGINS = {
  app: productSiteOrigin,
} as const;

type MarketingTransferPath = "/next/app/*" | "/next/login";
type MarketingDestinationHost = keyof typeof DESTINATION_ORIGINS;
type LocationParts = Pick<Location, "pathname" | "search" | "hash">;
type LoopMarker = { target: string; createdAt: number };

function pathMatches(pattern: MarketingTransferPath, pathname: string): boolean {
  if (!pattern.endsWith("/*")) return pathname === pattern;
  const family = pattern.slice(0, -2);
  return pathname === family || pathname.startsWith(`${family}/`);
}

function destinationPath(path: MarketingTransferPath, pathname: string): string {
  if (path === "/next/login") return "/login";
  return pathname.slice("/next/app".length) || "/practice";
}

export function resolveHostTransferDestination(
  path: MarketingTransferPath,
  destinationHost: MarketingDestinationHost,
  location: LocationParts,
): string | null {
  const destinationOrigin = DESTINATION_ORIGINS[destinationHost];
  if (!destinationOrigin || !pathMatches(path, location.pathname)) return null;

  const destination = new URL(destinationPath(path, location.pathname), destinationOrigin);
  destination.search = location.search;
  destination.hash = location.hash;
  return destination.href;
}

function readLoopMarker(): LoopMarker | null {
  const raw = sessionStorage.getItem("pp_marketing_host_transfer_v1");
  if (!raw) return null;
  const value = JSON.parse(raw) as Partial<LoopMarker>;
  return typeof value.target === "string" && typeof value.createdAt === "number"
    ? { target: value.target, createdAt: value.createdAt }
    : null;
}

function writeLoopMarker(marker: LoopMarker): void {
  sessionStorage.setItem("pp_marketing_host_transfer_v1", JSON.stringify(marker));
}

export function HostTransfer({
  path,
  destinationHost,
  location = window.location,
  replace = (target) => window.location.replace(target),
  readMarker = readLoopMarker,
  writeMarker = writeLoopMarker,
  now = Date.now,
}: {
  path: MarketingTransferPath;
  destinationHost: MarketingDestinationHost;
  location?: LocationParts;
  replace?: (target: string) => void;
  readMarker?: () => LoopMarker | null;
  writeMarker?: (marker: LoopMarker) => void;
  now?: () => number;
}) {
  const target = resolveHostTransferDestination(path, destinationHost, location);
  const [state, setState] = useState<"transferring" | "loop" | "invalid">(
    target ? "transferring" : "invalid",
  );
  const heading = useRef<HTMLHeadingElement>(null);
  const attemptedTarget = useRef<string | null>(null);

  useEffect(() => {
    if (!target) return;
    if (attemptedTarget.current === target) return;
    attemptedTarget.current = target;
    try {
      const timestamp = now();
      const marker = readMarker();
      if (
        marker?.target === target
        && timestamp >= marker.createdAt
        && timestamp - marker.createdAt <= HOST_TRANSFER_LOOP_TTL_MS
      ) {
        setState("loop");
        return;
      }
      writeMarker({ target, createdAt: timestamp });
      replace(target);
    } catch {
      setState("invalid");
    }
  }, [now, readMarker, replace, target, writeMarker]);

  useEffect(() => {
    if (state !== "transferring") heading.current?.focus();
  }, [state]);

  if (state === "transferring") {
    return <main><p role="status" aria-live="polite">Opening the Pinpoint app...</p></main>;
  }

  const loop = state === "loop";
  return (
    <main className="flex min-h-screen items-center justify-center p-6 text-center">
      <div className="max-w-lg">
        <h1 ref={heading} tabIndex={-1} className="text-3xl font-bold">
          {loop ? "We stopped a redirect loop" : "This transfer is not available"}
        </h1>
        <p className="mt-4 text-slate-600">
          {loop
            ? "Automatic navigation stopped so you can choose a safe destination."
            : "Pinpoint could not verify this app destination."}
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          {loop && target && <a href={target}>Continue to the app</a>}
          <a href="/">Home</a>
          <a href={productSiteUrl("/login")}>Log in</a>
        </div>
      </div>
    </main>
  );
}

export function LegacyAppHostTransfer() {
  return <HostTransfer path="/next/app/*" destinationHost="app" />;
}

export function LegacyLoginHostTransfer() {
  return <HostTransfer path="/next/login" destinationHost="app" />;
}

export default HostTransfer;
